import React from 'react'
import { Link } from 'gatsby'
import { IconInfo, IconLightBulb, IconSend } from '@posthog/icons'
import OSButton from 'components/OSButton'

interface DemoSlideProps {
    productHandle: string
    answers?: Array<{
        q: string
        url?: string
    }>
}

export default function DemoSlide({ productHandle, answers = [] }: DemoSlideProps) {
    return (
        <div className="h-full flex flex-col px-8 py-12 bg-gradient-to-b from-[#08080A] to-[#737385] text-white">
            <div>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 239 72" className="h-16 mx-auto">
                    <path
                        fill="#fff"
                        d="M222.154 29.936c-2.816 0-6.272 1.408-10.624 5.76-4.608 4.736-7.04 12.672-7.04 18.432 0 5.888 1.536 9.088 6.016 9.088 8.064 0 17.664-11.648 17.664-25.984 0-5.248-2.176-7.296-6.016-7.296Zm15.872 2.304c0 7.552-3.968 16.768-13.312 25.984-7.936 7.808-18.816 12.672-24.704 12.672-4.864 0-6.272-3.456-6.272-7.936 0-8.448 4.48-18.432 10.624-24.576 8.96-8.96 19.584-14.208 27.008-14.208 4.864 0 6.656 3.328 6.656 8.064ZM175.682 49.264c3.072-9.344 3.584-11.008 3.584-12.8 0-1.152-.64-1.536-1.664-1.536-1.92 0-8.576 4.224-14.976 10.368-5.504 5.376-8.576 12.544-9.984 17.792-2.944 3.2-9.856 5.76-11.648 5.76 5.632-17.536 8.192-26.88 8.192-31.104 0-1.92-.512-2.816-1.536-2.816-1.28 0-4.992 1.664-9.6 6.272-6.272 6.272-10.24 12.928-13.184 20.992-4.224 3.84-10.496 6.784-12.288 6.784 5.76-16.896 8.448-28.416 9.088-37.376 2.048-2.56 9.088-6.912 11.776-6.912 0 2.944-1.408 12.544-3.84 21.376 9.344-9.472 22.272-20.224 30.08-20.224 1.28 0 1.792 1.28 1.792 2.688 0 3.328-.512 6.4-3.712 17.536 6.144-6.144 25.856-21.12 32.384-21.12 2.176 0 2.432.512 2.432 2.048 0 2.176-1.792 6.4-4.992 14.976-2.048 5.376-3.84 12.672-3.84 14.592 0 2.048.384 3.84 2.048 4.48-3.968 4.864-11.264 8.96-14.976 8.96 0-3.584 1.408-10.24 4.864-20.736ZM86.027 47.344c-.512 1.92-1.024 4.992-1.024 7.808 0 2.176.512 4.352 1.536 5.376 1.152 1.28 2.944 1.792 5.248 1.792 4.736 0 11.776-4.48 19.456-12.16 0 .896 0 3.072-.256 3.84-7.808 7.808-21.76 16.384-31.232 16.384-2.56 0-4.992-2.304-4.992-7.808 0-10.624 3.84-20.736 10.752-27.52 8.32-8.32 18.688-11.264 23.424-11.264 3.328 0 4.864 1.792 4.864 3.328 0 2.816-1.792 5.888-3.584 7.552-6.4 6.4-15.616 11.008-24.192 12.672Zm16.128-9.472c1.28-1.28 2.304-3.456 2.304-4.864 0-2.432-1.408-4.096-4.608-4.096-1.664 0-4.48.896-6.4 2.688-3.072 3.072-5.376 7.936-6.656 12.288 2.176 0 11.648-2.304 15.36-6.016ZM.954 70.64c1.792-9.856 12.032-38.016 19.456-53.12 3.456-2.688 9.984-4.608 13.184-4.608-3.072 5.504-6.912 14.208-10.496 23.424-3.84 9.984-6.912 18.304-8.32 25.472-.256 1.536.64 1.92 2.688 2.56 1.92.64 4.864 1.024 8.064 1.024 23.168 0 37.632-17.664 37.632-35.968 0-6.144-2.688-10.624-6.784-13.824-7.424-5.632-19.584-7.296-30.208-7.168-4.992 0-13.568.384-17.408.768.512-1.408 6.528-6.272 10.112-7.808 3.456-.384 8.064-.64 12.8-.64 27.264 0 43.008 8.704 43.008 24.832 0 22.272-26.368 45.952-63.232 45.952-3.328 0-8.576-.256-10.496-.896Z"
                    />
                </svg>

                <h2 className="sr-only">Demo</h2>
            </div>

            <div className="flex-1 grid @2xl:grid-cols-2 gap-6 my-12">
                {answers.map((answer, index) => {
                    const encodedText = encodeURIComponent(answer.q.toLowerCase())
                    const maxUrl = `https://app.posthog.com/#panel=max:${encodedText}`

                    return (
                        <OSButton
                            asLink
                            key={index}
                            zoomHover="lg"
                            onClick={() => window.open(maxUrl, '_blank')}
                            className="flex gap-2 justify-between items-center p-2 border border-input rounded-full text-left w-full active:bg-transparent"
                        >
                            <span className="text-xl text-center italic font-medium w-full pl-7 text-white">
                                {answer.q}
                            </span>
                            <span>
                                <div className="p-2 rounded-full bg-blue">
                                    <IconSend className="size-6 text-white" />
                                </div>
                            </span>
                        </OSButton>
                    )
                })}
            </div>

            <div className="flex gap-2 justify-center">
                <IconLightBulb className="size-10 opacity-50" />
                <div className="flex flex-col text-xl">
                    <strong>Signed into PostHog?</strong>
                    <p>Click any question to get PostHog AI started.</p>
                </div>
            </div>
        </div>
    )
}
